import { Caption } from './types/CaptionInterfaces';

// Provider types for image/video generation
export type ImageProvider = 'runway' | 'sdxl' | 'banana_nano';
export type VideoProvider = 'runway'; // Only runway for video

export interface ProviderConfig {
  endpoint: string;
  default: boolean;
  maxImages: number;
  supportedRatios: string[];
  features: string[];
}

// src/types/index.ts
export * from './types/CaptionInterfaces';

export interface TranscriptionSegment {
    start: number;
    end: number;
    text: string;
  }
  
  export interface TranscriptionData {
    text: string;
    segments: TranscriptionSegment[];
  }

export interface CaptionResponse {
    videoPath: string;
    data: Omit<Caption, 'id'>[];
    projectId: string;  // Making projectId required
}

export interface ModalData {
  segment?: Caption;
  isOpen: boolean;
  onClose: () => void;
  targetId: string;
  mediaItem?: MediaItem
}

export interface LibraryItem {
  id: string;
  url: string;
  name: string;
  type: 'image' | 'video' | 'audio';
  source: 'ai-generated' | 'uploaded';
  createdAt: string;
  metadata?: any;
  duration?: number; // For videos and audio
  prompt?: string; // For AI-generated content
  seed?: number; // For AI-generated content
}

// ============================================================================
// CHUNKED UPLOAD TYPES
// ============================================================================

export interface ChunkedUploadSession {
  sessionId: string;
  fileName: string;
  fileSize: number;
  totalChunks: number;
  uploadedChunks: number[];
  status: 'uploading' | 'assembling' | 'completed' | 'failed';
  createdAt: string;
  message?: string;
  error?: string;
}

export interface ChunkUploadResult {
  chunkIndex: number;
  success: boolean;
  sessionId: string;
  message?: string;
  error?: string;
}

export interface ChunkedUploadProgress {
  stage: 'chunking' | 'uploading' | 'assembling' | 'processing';
  currentChunk?: number;
  totalChunks?: number;
  percentage: number;
  message: string;
  bytesUploaded?: number;
  totalBytes?: number;
  estimatedTimeRemaining?: number;
}

export interface CaptionResponse {
    videoPath: string;
    data: Omit<Caption, 'id'>[];
    projectId: string;  // Making projectId required
}

export interface ModalData {
  segment?: Caption;
  isOpen: boolean;
  onClose: () => void;
  targetId: string;
  mediaItem?: MediaItem
}

export enum SpecialEffect {
  NONE = 'none',
  FADEOUT = 'fade-out',
  FADEIN = 'fade-in',
  ZOOMOUT = 'zoom-out',
  ZOOMIN = 'zoom-in',
  PANLEFT = 'pan-left',
  PANLEFTTOP = 'pan-left-top',
  PANRIGHT = 'pan-right',
  PANRIGHTTOP = 'pan-right-top',
  PANUP = 'pan-up',
  PANDOWN = 'pan-down',
  ZOOMPANRIGHT = 'zoom-pan-right',
  ZOOMPANLEFT = 'zoom-pan-left',
  ARCHIVED = 'archived',
  CLEANUP_KEPT = 'cleanup-kept',
  CLEANUP_REMOVED = 'cleanup-removed',
  SLIDE_IN = 'slide-in',
  BOUNCE = 'bounce',
  WAVE = 'wave',
  SPIN_ZOOM = 'spin-zoom',
  PULSE = 'pulse',

}

export interface MediaItem {
  id: string;
  name: string;
  start: number;
  end: number;
  url: string;
  type: string;
  length: number;
  src: string;
  effect?: SpecialEffect;
  hidden?: boolean; // Track visibility state for hide/disable functionality
  isLoaded?: boolean; // Temporary UI-only field for lazy loading state (not persisted)
  color?: string; // Hex color for video color effect/tint (e.g., "#FF0000")
  
  // Video effects (for color grading and filters)
  saturation?: number; // Saturation level (0 = B&W, 1 = normal, 2 = super saturated) - 0 to 2
  sepia?: number; // Sepia intensity (0 = off, 0.5 = subtle, 1 = full sepia) - 0 to 1
  vignette?: number; // Vignette intensity (0 = off, 0.5 = subtle, 1 = strong) - 0 to 1
  contrast?: number; // Contrast adjustment (1 = normal, 0.5 = low, 2 = high) - 0.5 to 2
  brightness?: number; // Brightness adjustment (1 = normal, 0.5 = dark, 1.5 = bright) - 0.5 to 2
  
  // Transition effects (applied when transitioning to NEXT clip)
  transitionType?: string; // Transition type: 'fade', 'wipeleft', 'dissolve', etc.
  transitionDuration?: number; // Duration in seconds (0.3 to 3, default: 0.5)
  
  // Video trimming (for split functionality)
  videoOffset?: number; // Seconds to skip from start of video file (for split parts)
  videoDuration?: number; // How long to play from the video file
  
  // Aspect ratio conversion adjustments (optional - for 16:9 ↔ 9:16 conversion)
  position?: { 
    x: number;  // Horizontal offset (-100 to 100, percentage)
    y: number;  // Vertical offset (-100 to 100, percentage)
  };
  scale?: { 
    scaleX: number;  // Horizontal scale (0.1 to 3.0)
    scaleY: number;  // Vertical scale (0.1 to 3.0)
  };
}

// Define the response types
export interface UploadedFile {
  url: string;
  name: string;
}

export interface UploadResponse {
  files: UploadedFile[];
  projectId: string;
}

export interface UploadResult {
  success: boolean;
  urls: string[];
  projectId: string;
  uploadedFiles: UploadedFile[];
  files: UploadedFile[];
}

export interface UploadAudioResponse {
  file: { url: string; name: string };
  projectId: string;
  jobId?: string;
  data?: any;
}

export interface  UploadVideoCaptionResponse {
  file: { url: string; name: string };
  projectId: string;
    
}

export interface MediaSaveRespose{
    url:string;
    fileType: string;
    name: string
}

export enum StepIndicatorNames {
  IdeaCreation = 'IdeaCreation',
  ScriptGeneration = 'ScriptGeneration',
  AudioGeneration = 'AudioGeneration',
  ContentCreation = 'ContentCreation',
  CaptionCreation = 'CaptionCreation',
  SoundGeneration = 'SoundGeneration',
  TitleDescTag = 'TitleDescTag',
  ThumbnailCreation = 'ThumbnailCreation',
  Export = 'Export'
}

// Re-export thumbnail types
export * from './types/thumbnailTypes';

// Re-export aspect ratio conversion types
export * from './types/aspectRatioConversion';
